import { motion, AnimatePresence } from 'framer-motion';
import { HiX, HiTrash, HiOutlineShoppingBag, HiOutlineShieldCheck } from 'react-icons/hi';
import { FaWhatsapp } from 'react-icons/fa';
import { useCart } from '../context/CartContext';
import { Link } from 'react-router-dom';
import ScrollTopTop from '../components/ui/ScrollToTop';

// Sub-componente de Pagos más equilibrado
const PaymentMethods = ({ className }) => (
    <div className={`mt-4 pt-6 border-t border-gray-100 ${className}`}>
        <p className="text-[9px] font-black text-gray-400 uppercase tracking-[0.2em] mb-4 text-center">
            Pagos Seguros y Encriptados
        </p>

        <div className="flex flex-wrap items-center justify-center gap-4 md:gap-6 transition-all duration-500 opacity-70 hover:opacity-100">
            <img src="https://images.seeklogo.com/logo-png/39/1/yape-app-logo-png_seeklogo-399697.png" alt="Yape" className="h-6 md:h-8 w-auto object-contain" />
            <img src="https://images.seeklogo.com/logo-png/38/1/plin-logo-png_seeklogo-386806.png" alt="Plin" className="h-5 md:h-7 w-auto object-contain" />
            <img src="https://upload.wikimedia.org/wikipedia/commons/d/d3/Visa_Inc._logo_%282005%E2%80%932014%29.png" alt="Visa" className="h-3 md:h-4 w-auto object-contain" />
            <img src="https://upload.wikimedia.org/wikipedia/commons/thumb/b/b5/PayPal.svg/1200px-PayPal.svg.png" alt="PayPal" className="h-4 md:h-6 w-auto object-contain" />
        </div>

        <div className="mt-5 flex items-start gap-2 py-3 px-4 bg-gray-50 rounded-xl border border-gray-100">
            <HiOutlineShieldCheck className="text-[#F26B1D] text-lg mt-0.5" />
            <span className="text-[8px] md:text-[9px] font-bold uppercase tracking-tight text-gray-500 leading-tight">
                Garantía Planix: Envío inmediato a tu correo con soporte técnico dedicado.
            </span>
        </div>
    </div>
);

const CartDrawer = () => {
    const { cart, isCartOpen, setIsCartOpen, removeFromCart, cartTotal } = useCart();

    const handleCheckout = () => {
        const WHATSAPP_NUMBER = "51983974833";

        // Mapeamos los items incluyendo el ID (p.id) de forma visible y profesional
        const items = cart.map(p =>
            `- *${p.name}* [ID: ${p.id}] [ID: ${p.identity}] (Las ${p.cantidLaminas} laminas) (S/ ${p.price})`
        ).join('%0A');

        // Construimos el mensaje con una estructura más limpia
        const msg = `¡Hola Planix! 👋%0A%0ADeseo adquirir los siguientes planos:%0A${items}%0A%0A*Total a pagar: S/ ${cartTotal}*%0A%0A¿Cuáles son los pasos para la entrega?`;

        // Abrimos WhatsApp
        window.open(`https://wa.me/${WHATSAPP_NUMBER}?text=${msg}`, '_blank');
    };

    return (
        <AnimatePresence>
            <ScrollTopTop />
            {isCartOpen && (
                <>
                    {/* Overlay */}
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={() => setIsCartOpen(false)}
                        className="fixed inset-0 bg-[#1C1C1C]/60 backdrop-blur-sm z-[2000]"
                    />

                    {/* Panel Lateral */}
                    <motion.div
                        initial={{ x: '100%' }}
                        animate={{ x: 0 }}
                        exit={{ x: '100%' }}
                        transition={{ type: 'spring', damping: 30, stiffness: 300 }}
                        className="fixed right-0 top-0 h-full w-full max-w-md bg-white z-[2001] shadow-2xl flex flex-col overflow-hidden"
                    >
                        {/* Header Fijo */}
                        <div className="p-6 border-b border-gray-100 flex justify-between items-center bg-white">
                            <div className="flex items-center gap-2">
                                <div className="relative">
                                    <HiOutlineShoppingBag className="text-2xl text-[#F26B1D]" />
                                    <span className="absolute -top-1 -right-1 bg-[#1C1C1C] text-white text-[8px] w-4 h-4 flex items-center justify-center rounded-full font-black">
                                        {cart.length}
                                    </span>
                                </div>
                                <h2 className="text-xl font-black text-[#1C1C1C] tracking-tighter">Mi Pedido</h2>
                            </div>
                            <button
                                onClick={() => setIsCartOpen(false)}
                                className="p-2 hover:bg-gray-100 rounded-full transition-colors text-gray-400 hover:text-[#1C1C1C]"
                            >
                                <HiX className="text-2xl" />
                            </button>
                        </div>

                        {/* Cuerpo Scrollable */}
                        <div className="flex-1 overflow-y-auto p-6 scrollbar-hide">
                            {cart.length === 0 ? (
                                <div className="h-full flex flex-col items-center justify-center text-center space-y-4">
                                    <div className="w-20 h-20 bg-gray-50 rounded-full flex items-center justify-center">
                                        <HiOutlineShoppingBag className="text-3xl text-gray-200" />
                                    </div>
                                    <p className="text-gray-400 font-bold text-sm uppercase tracking-widest">Tu carrito está vacío</p>
                                    <button
                                        onClick={() => setIsCartOpen(false)}
                                        className="text-[#F26B1D] font-black text-xs uppercase tracking-tighter border-b-2 border-[#F26B1D]"
                                    >
                                        Explorar Catálogo
                                    </button>
                                </div>
                            ) : (
                                <div className="space-y-4">
                                    {cart.map((item) => (
                                        <motion.div
                                            layout
                                            initial={{ opacity: 0, y: 10 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            key={item.id}
                                            className="flex gap-4 p-3 bg-white rounded-2xl border border-gray-100 hover:border-[#F26B1D]/20 transition-all group"
                                        >
                                            <div className="relative overflow-hidden rounded-xl h-20 w-20 flex-shrink-0">
                                                <img src={item.images[0]} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" alt={item.name} />
                                            </div>
                                            <div className="flex flex-col justify-between py-1 flex-1">
                                                <div>
                                                    <h4 className="font-bold text-[#1C1C1C] text-xs leading-tight line-clamp-2 uppercase tracking-tight">{item.name}</h4>
                                                    <p className="text-[#F26B1D] font-black text-sm mt-1">S/ {item.price}</p>
                                                </div>
                                                <button
                                                    onClick={() => removeFromCart(item.id)}
                                                    className="text-[9px] text-gray-400 font-black uppercase tracking-widest flex items-center gap-1 hover:text-red-500 transition-colors"
                                                >
                                                    <HiTrash /> Quitar
                                                </button>
                                            </div>
                                        </motion.div>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* Footer Fijo con Total y Pagos */}
                        {cart.length > 0 && (
                            <div className="p-6 border-t border-gray-100 bg-gray-50/50 backdrop-blur-md">
                                <div className="flex justify-between items-end mb-6">
                                    <span className="text-gray-400 text-[10px] font-black uppercase tracking-[0.2em]">Total Estimado</span>
                                    <span className="text-3xl font-black text-[#1C1C1C] tracking-tighter">S/ {cartTotal}</span>
                                </div>

                                <button
                                    onClick={handleCheckout}
                                    className="w-full bg-[#25D366] hover:bg-[#128C7E] text-white py-4 rounded-2xl font-black flex items-center justify-center gap-3 shadow-xl shadow-[#25D366]/20 transition-all active:scale-95 uppercase text-xs tracking-[0.2em]"
                                >
                                    <FaWhatsapp className="text-lg" />
                                    Hacer pedido 
                                </button>

                                <Link
                                    to="/metodos-de-pago"
                                    className="block mt-4 
                                text-center text-[#F26B1D] 
                                font-black text-xs uppercase 
                                tracking-tighter border-b-2 
                                border-[#F26B1D] py-2"
                                    onClick={() => setIsCartOpen(false)}
                                >
                                    Ver Métodos de Pago
                                </Link>

                                <PaymentMethods />
                            </div>
                        )}
                    </motion.div>
                </>
            )}
        </AnimatePresence>
    );
};

export default CartDrawer;